// The wizard's four steps above the current one: done, current or still ahead. A done step names
// what was chosen there and goes back to it; moving forward goes only through Avançar, so no step's
// check is skipped. Each step's card opens with its moves (Voltar, what is missing, Avançar) in a
// bar that stays in view while the step scrolls.
import { Option } from 'effect';
import { useId } from 'react';
import type { ReactNode, Ref } from 'react';
import { Button } from '@finnest/ui';
import { WIZARD_STEPS, WIZARD_STEP_TITLES } from '../wizard-state.ts';
import type { DoneStepKind, WizardStepKind } from '../wizard-state.ts';

const STEP_STATES = ['done', 'current', 'next'] as const;
type StepState = (typeof STEP_STATES)[number];

const stateOf = (step: WizardStepKind, current: WizardStepKind): StepState => {
  const currentIndex = WIZARD_STEPS.indexOf(current);
  const index = WIZARD_STEPS.indexOf(step);
  if (index < currentIndex) return 'done';
  return index === currentIndex ? 'current' : 'next';
};

const ARIA_CURRENT: Readonly<Record<StepState, 'step' | false>> = {
  done: false,
  current: 'step',
  next: false,
};

// The steps whose dot is a check mark instead of the step's number.
const SHOWS_CHECK: Readonly<Record<StepState, boolean>> = {
  done: true,
  current: false,
  next: false,
};

/** A check mark, for a done step or a chosen option. */
export const CheckMark = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
    <path d="M5 12l5 5 9-10" />
  </svg>
);

export const WizardStepper = ({
  current,
  chosen,
  onGoTo,
}: {
  readonly current: WizardStepKind;
  /** What each done step chose, shown under its title; none where there is nothing to name. */
  readonly chosen: Readonly<Record<DoneStepKind, Option.Option<string>>>;
  /** Goes back to a done step; null while nothing may move (a create is being sent). */
  readonly onGoTo: ((kind: DoneStepKind) => void) | null;
}): JSX.Element => (
  <ol className="journey-stepper" aria-label="Etapas">
    {WIZARD_STEPS.map((step, index) => {
      const state = stateOf(step, current);
      const done = step === 'review' || state !== 'done' ? null : step;
      const { title, about } = WIZARD_STEP_TITLES[step];
      return (
        <li key={step} data-state={state} aria-current={ARIA_CURRENT[state]}>
          <button
            type="button"
            className="journey-stepper-step"
            disabled={done === null || onGoTo === null}
            onClick={() => {
              if (done !== null) onGoTo?.(done);
            }}
          >
            <span className="journey-stepper-dot" aria-hidden="true">
              {SHOWS_CHECK[state] ? <CheckMark /> : index + 1}
            </span>
            <span>
              <strong>{title}</strong>
              <small>{done === null ? about : Option.getOrElse(chosen[done], () => about)}</small>
            </span>
          </button>
        </li>
      );
    })}
  </ol>
);

/** How the operator leaves a step: back (none on the first step) and forward. */
export type StepMoves = {
  readonly back: { readonly disabled: boolean; readonly onBack: () => void } | null;
  readonly next: {
    readonly label: string;
    readonly disabled: boolean;
    /** Why it is disabled, tied to the button; null while nothing holds it back. */
    readonly hint: string | null;
    readonly onNext: () => void;
    /** Whether it leaves for the transmitter, marked with a chevron. */
    readonly onward?: boolean;
  };
  /** What the bar says while nothing holds Avançar back. */
  readonly note?: ReactNode;
};

const Chevron = ({ direction }: { readonly direction: 'left' | 'right' }): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
    <path d={direction === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
  </svg>
);

// One step: its moves on top, then its title (focused when the wizard arrives at it) and its fields.
export const StepFrame = ({
  heading,
  title,
  lead,
  moves,
  children,
}: {
  readonly heading: Ref<HTMLHeadingElement>;
  readonly title: string;
  readonly lead: string;
  readonly moves: StepMoves;
  readonly children: ReactNode;
}): JSX.Element => {
  const { back, next, note } = moves;
  const titleId = useId();
  const hint = useId();
  return (
    <section className="journey-step" aria-labelledby={titleId}>
      <div className="journey-moves">
        {back === null ? null : (
          <Button disabled={back.disabled} onClick={back.onBack}>
            <Chevron direction="left" />
            Voltar
          </Button>
        )}
        {next.hint === null ? (
          <span className="journey-moves-hint">{note}</span>
        ) : (
          <span id={hint} className="journey-moves-hint">
            {next.hint}
          </span>
        )}
        <Button
          variant="primary"
          disabled={next.disabled}
          aria-describedby={next.hint === null ? undefined : hint}
          onClick={next.onNext}
        >
          {next.label}
          {next.onward === true ? <Chevron direction="right" /> : null}
        </Button>
      </div>
      <div className="journey-step-body">
        <header>
          <h2 id={titleId} ref={heading} tabIndex={-1}>
            {title}
          </h2>
          <p>{lead}</p>
        </header>
        {children}
      </div>
    </section>
  );
};
